import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import type { AppAbility } from '@shared/types/ability';
import { canAccessAnyAnalytics } from '@shared/lib';
import { useAbility } from '@features/authorization';
import { useAuth } from '@features/auth';
import { Header } from '@widgets/header';

type AnalyticsRouteContext = {
  auth: { isLoading: boolean; isAuthenticated: boolean };
  ability: AppAbility;
};

/**
 * Analytics Section Layout
 *
 * Route: /:locale/analytics/*
 * Wraps all analytics pages with dashboard layout
 *
 * This file is automatically code-split by TanStack Router.
 */
export const Route = createFileRoute('/$locale/_authenticated/analytics')({
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as unknown as AnalyticsRouteContext;

    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    if (!canAccessAnyAnalytics(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/unauthorized',
        params: { locale: params.locale },
      });
    }
  },
  component: AnalyticsLayout,
});

function AnalyticsLayout() {
  const { locale: rawLocale } = Route.useParams();
  const locale = rawLocale as unknown as string;
  const navigate = useNavigate();

  const ability = useAbility();
  const auth = useAuth();

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    if (!canAccessAnyAnalytics(ability)) {
      void navigate({
        to: '/$locale/unauthorized',
        params: { locale },
        replace: true,
      });
    }
  }, [ability, auth.isAuthenticated, auth.isLoading, locale, navigate]);

  return (
    <div className="flex min-h-full flex-col pt-16 min-[992px]:pt-0">
      <Header locale={locale} className="min-[992px]:hidden" />

      <main className="min-w-0 p-6">
        <Outlet />
      </main>
    </div>
  );
}
